/**
 * Modal centralizada para edição de contração individual
 * 
 * Decisões de UI/UX:
 * - Layout compacto e minimalista
 * - Foco na edição rápida
 * - Feedback visual imediato
 * - Opções em grid 2x2
 */

import React, { useEffect, useRef } from 'react';
import { XIcon as X } from '../../icons';
import { contractionOptions } from '../types/objects';
import { Button } from '../../ui';

interface ContractionModalProps {
  objectName: string;
  currentValue: string;
  onClose: () => void;
  onChange: (value: string) => void;
}

export const ContractionModal: React.FC<ContractionModalProps> = ({
  objectName,
  currentValue,
  onClose,
  onChange
}) => {
  // Evitar "salto" de renderização: renderizar modal já centralizada, sem animar translate depois
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Forçar layout antes de exibir (evita flash em posição errada em alguns hosts)
    const node = dialogRef.current;
    if (node) {
      // trigger reflow
      void node.offsetHeight;
    }
  }, []);
  return (
    <>
      {/* Overlay com blur suave */}
      <div 
        className="fixed inset-0 bg-navy-900/90 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Modal compacta e centralizada */}
      <div 
        ref={dialogRef}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[260px] bg-navy-800 rounded-lg shadow-xl z-50 will-change-transform"
      >
        {/* Cabeçalho minimalista */}
        <div className="flex items-center justify-between p-3 border-b border-navy-700">
          <div className="flex-1 min-w-0">
            <h4 className="text-white text-sm font-medium truncate" title={objectName}>
              {objectName}
            </h4>
            <div className="text-xs text-gray-400 mt-0.5">
              Contração atual: <span className="text-gray-300">{currentValue}</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white rounded-lg hover:bg-navy-700/50"
            icon={X}
          />
        </div>

        {/* Grid de opções 2x2 */}
        <div className="p-3">
          <div className="grid grid-cols-2 gap-2">
            {contractionOptions.map((option) => (
              <Button
                key={option}
                variant="ghost"
                size="sm"
                onClick={() => onChange(option)}
                className={`flex items-center justify-center px-3 py-2 rounded text-sm transition-colors
                  ${option === currentValue 
                    ? 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/50' 
                    : 'bg-navy-700/50 text-gray-300 hover:bg-navy-700 hover:text-white'
                  }`}
              >
                {option}
              </Button>
            ))}
          </div>
        </div>

        {/* Rodapé com ações */}
        <div className="flex items-center justify-end gap-2 p-2 border-t border-navy-700">
          <Button
            variant="ghost"
            size="xs"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="xs"
            onClick={() => onChange(currentValue)}
          >
            Aplicar
          </Button>
        </div>
      </div>
    </>
  );
}; 